import { useEffect } from 'react';
import { Download, FileSignature, X } from 'lucide-react';
import type { IssuedKitItem } from '@hcc/types';
import { formatDateTime } from '../../lib/dateFmt';

/**
 * Modal viewer for a captured signature PNG. Reused by:
 *   - EventTimeline: any row whose event has a signature_s3_key
 *   - StationView "Issued" rows: the latest KitIssued signature for
 *     a person at this station
 *
 * The image is fetched from /api/signatures/:key by <img src>; the
 * existing route handles RBAC and the audit-access middleware logs
 * the read. We don't proxy the bytes through this component.
 */
export interface SignatureModalProps {
  /** S3 key from event.signature_s3_key or stations[s].signature_key. */
  signatureKey: string;
  /** Headline (e.g. "PPE kit issued"). */
  title: string;
  /** ISO timestamp of the event. */
  whenISO: string;
  /** Optional items list — surfaces what was signed for. */
  items?: readonly IssuedKitItem[];
  /** Optional sub-line — actor name, station, etc. */
  subtitle?: string;
  onClose: () => void;
}

export function SignatureModal({
  signatureKey,
  title,
  whenISO,
  items,
  subtitle,
  onClose,
}: SignatureModalProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const url = `/api/signatures/${encodeURIComponent(signatureKey)}`;
  const when = new Date(whenISO);
  const filename = `signature-${signatureKey.replace(/[^a-z0-9-]+/gi, '_')}.png`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="signature-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-fg/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />
      <div className="aurora-fade-in flex w-full max-w-2xl flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="flex items-start justify-between gap-3 border-b border-divider/10 bg-surface-2 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <FileSignature className="mt-0.5 h-5 w-5 shrink-0 text-fg-muted" aria-hidden />
            <div className="min-w-0">
              <h2
                id="signature-title"
                className="text-base font-semibold tracking-tight text-fg"
              >
                {title}
              </h2>
              <p className="text-xs text-fg-muted">
                {formatDateTime(when)}
                {subtitle ? <> · {subtitle}</> : null}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-token-sm p-1 text-fg-muted transition-colors hover:bg-surface hover:text-fg"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex items-center justify-center bg-surface-inset p-4">
          {/* The image element does the fetch. Background grid mimics
              a signature pad so it's clear the white area is the
              signed canvas. The grid is white-on-white intentionally —
              we always want the signature itself on a literal white
              card, regardless of theme, so signatures stay legible
              after download. */}
          <div
            className="flex w-full items-center justify-center rounded-token-sm border border-line/10 bg-surface"
            style={{
              minHeight: '200px',
              backgroundImage:
                'repeating-linear-gradient(0deg, #f1f5f9 0 1px, transparent 1px 24px), repeating-linear-gradient(90deg, #f1f5f9 0 1px, transparent 1px 24px)',
            }}
          >
            <img
              src={url}
              alt="Captured signature"
              className="max-h-[60vh] w-auto max-w-full object-contain p-4"
              draggable={false}
            />
          </div>
        </div>

        {items && items.length > 0 ? (
          <div className="border-t border-divider/10 px-4 py-3">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-dim">
              Items signed for
            </div>
            <ul className="space-y-0.5 text-xs text-fg">
              {items.map((it, idx) => (
                <li key={idx} className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{it.name}</span>
                  {it.quantity && it.quantity > 1 ? (
                    <span className="text-fg-muted">×{it.quantity}</span>
                  ) : null}
                  {it.size ? <span className="text-fg-muted">size {it.size}</span> : null}
                  {it.registration ? (
                    <span className="font-mono text-[11px] text-fg-muted">
                      {it.registration}
                    </span>
                  ) : it.serial ? (
                    <span className="font-mono text-[11px] text-fg-muted">
                      SN {it.serial}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <footer className="flex items-center justify-between gap-3 border-t border-divider/10 bg-surface-2 px-4 py-3">
          <span className="text-[11px] text-fg-dim">
            Stored on the server. Every view is logged in the access log.
          </span>
          <a
            href={url}
            download={filename}
            className="inline-flex items-center gap-1.5 rounded-token-sm bg-accent px-3 py-1.5 text-xs font-medium text-fg-on-accent transition-colors hover:bg-accent-hover"
          >
            <Download className="h-3.5 w-3.5" />
            Download PNG
          </a>
        </footer>
      </div>
    </div>
  );
}
